import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Parent Login Page
 * Parents can login with email/password to access their dashboard
 * Preserves redirect URL (including gmail=connected) after login
 */
export const ParentLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { loginAsParent } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Store the intended redirect URL from location state or URL params
  const [intendedRedirect, setIntendedRedirect] = useState(null);
  
  useEffect(() => {
    // Check if there's a stored redirect URL (from OAuth callback)
    const storedRedirect = sessionStorage.getItem('post_login_redirect');
    if (storedRedirect) {
      setIntendedRedirect(storedRedirect);
    } else if (location.state?.from) {
      // React Router's redirect state
      setIntendedRedirect(location.state.from);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (data.ok && data.access_token) {
        loginAsParent(data.access_token, data.family_id, data.parent_id, data.full_name);
        toast.success('Welcome back!');
        
        // Clear stored redirect
        sessionStorage.removeItem('post_login_redirect');
        
        // If we have an intended redirect with gmail=connected, use it
        if (intendedRedirect && intendedRedirect.includes('gmail=')) {
          // Parse and rebuild URL with correct family_id
          const url = new URL(intendedRedirect, window.location.origin);
          // Ensure family_id is set correctly from login response
          if (data.family_id) {
            url.searchParams.set('family_id', data.family_id);
          }
          navigate(url.pathname + url.search);
        } else {
          // Default: go to parent dashboard with family_id
          navigate(`/parent?family_id=${data.family_id}`);
        }
      } else {
        setError(data.detail || data.error || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5E6D3] via-[#FAFAF8] to-[#F2F7F5] p-4">
      <Card className="w-full max-w-md shadow-sm border-slate-100">
        <CardHeader className="text-center pb-4">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-slate-400 hover:text-slate-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to home
          </Link>
          <div className="mx-auto w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center mb-4">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="text-xl font-heading">Welcome back</CardTitle>
          <CardDescription className="text-slate-500">
            Sign in to see how your child's day is going
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600 text-sm font-medium">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                disabled={loading}
                className="border-slate-200 focus:border-primary focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-600 text-sm font-medium">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                disabled={loading}
                className="border-slate-200 focus:border-primary focus:ring-primary"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary-800 text-white rounded-xl py-2.5"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:text-primary-800 font-medium transition-colors">
                Sign up free
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentLogin;
